import React, { useRef } from 'react';
import { Box, Typography } from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';

type FaqItem = { question: string; answer?: string };

type Props = {
  section: string;
  items: FaqItem[];
  openKey: string | null;
  onToggle: (key: string) => void;
};

const FaqAccordion: React.FC<Props> = ({ section, items, openKey, onToggle }) => {
  const focusItem = (idx: number) => {
    const id = `${section}-${idx}-label`;
    const el = document.getElementById(id) as HTMLElement | null;
    if (el) el.focus();
  };

  const focusHeaderByKey = (key: string) => {
    // Delay to allow React to update DOM
    setTimeout(() => {
      const el = document.getElementById(`${key}-label`) as HTMLElement | null;
      if (el) el.focus();
    }, 0);
  };

  const focusNextHeaderInDocument = (currentId: string) => {
    const buttons = Array.from(document.querySelectorAll('button[data-faq]')) as HTMLElement[];
    if (!buttons.length) return false;
    const idx = buttons.findIndex((b) => b.id === currentId);
    // Do not wrap by default: if at last, return false (no movement)
    if (idx === -1 || idx === buttons.length - 1) return false;
    const next = idx + 1;
    const el = buttons[next];
    if (el) {
      el.focus();
      return true;
    }
    return false;
  };

  const focusPrevHeaderInDocument = (currentId: string) => {
    const buttons = Array.from(document.querySelectorAll('button[data-faq]')) as HTMLElement[];
    if (!buttons.length) return false;
    const idx = buttons.findIndex((b) => b.id === currentId);
    // Do not wrap by default: if at first, return false (no movement)
    if (idx <= 0) return false;
    const prev = idx - 1;
    const el = buttons[prev];
    if (el) {
      el.focus();
      return true;
    }
    return false;
  };

  const handleHeaderKeyDown = (
    e: React.KeyboardEvent,
    idx: number,
    key: string,
    isOpen: boolean
  ) => {
    const max = items.length - 1;
    switch (e.key) {
      case 'ArrowDown': {
        // Primary behavior: expand the focused header if closed
        if (!isOpen) {
          e.preventDefault();
          onToggle(key);
          focusHeaderByKey(key);
        } else {
          // If already open, try to move focus to next header in document order.
          // If there is no next header (we're at the end), don't prevent default so the page can scroll.
          const currentId = `${section}-${idx}-label`;
          const moved = focusNextHeaderInDocument(currentId);
          if (moved) e.preventDefault();
        }
        break;
      }
      case 'ArrowUp': {
        // Primary behavior: close the focused header if open
        if (isOpen) {
          e.preventDefault();
          onToggle(key);
          focusHeaderByKey(key);
        } else {
          // If already closed, try to move focus to previous header. If none, allow default behavior.
          const currentId = `${section}-${idx}-label`;
          const moved = focusPrevHeaderInDocument(currentId);
          if (moved) e.preventDefault();
        }
        break;
      }
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(max);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      {items.map((faq, idx) => {
        const key = `${section}-${idx}`;
        const isOpen = openKey === key;

        const AccordionItem: React.FC = () => {
          const contentRef = useRef<HTMLDivElement | null>(null);

          return (
            <Box key={key} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Box
                component="button"
                type="button"
                data-faq={section}
                aria-expanded={isOpen}
                aria-controls={`${key}-content`}
                id={`${key}-label`}
                onClick={() => onToggle(key)}
                onKeyDown={(e: React.KeyboardEvent) => handleHeaderKeyDown(e, idx, key, isOpen)}
                sx={{
                  display: 'flex',
                  width: '100%',
                  textAlign: 'left',
                  padding: { xs: '16px', sm: '24px' },
                  boxSizing: 'border-box',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0',
                  alignSelf: 'stretch',
                  borderRadius: '6px',
                  border: '1px solid var(--Neutral-neutral-300, #D9D9D9)',
                  background: '#FFF',
                  cursor: 'pointer',
                  '&:focus': { boxShadow: '0 0 0 3px rgba(3,169,244,0.18)' },
                  // reset native button styles
                  appearance: 'none',
                  borderStyle: 'solid',
                  borderWidth: '1px',
                  '&:hover': { background: '#FAFAFB' }
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    whiteSpace: 'normal'
                  }}
                >
                  {faq.question}
                </Typography>
                <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} style={{ width: 16, height: 16, color: '#151618', marginLeft: 8 }} />
              </Box>

              <Box
                id={`${key}-content`}
                role="region"
                aria-labelledby={`${key}-label`}
                hidden={!isOpen}
                tabIndex={isOpen ? 0 : -1}
                ref={contentRef}
                onKeyDown={(e: React.KeyboardEvent) => {
                  const target = e.currentTarget as HTMLElement;
                  if (e.key === 'ArrowUp') {
                    if (target.scrollTop === 0) {
                      e.preventDefault();
                      onToggle(key); // close panel when at top
                      // move focus back to header
                      const headerId = `${key}-label`;
                      const header = document.getElementById(headerId) as HTMLElement | null;
                      if (header) header.focus();
                    }
                  } else if (e.key === 'ArrowDown') {
                    // if at bottom, move focus to next header
                    if (target.scrollHeight - target.clientHeight === target.scrollTop) {
                      e.preventDefault();
                        const currentHeaderId = `${section}-${idx}-label`;
                        focusNextHeaderInDocument(currentHeaderId);
                    }
                  }
                }}
                sx={{
                  mt: 1,
                  px: { xs: '16px', sm: '24px' },
                  maxHeight: '320px',
                  overflowY: 'auto',
                  pr: 1,
                  boxSizing: 'border-box'
                }}
              >
                {faq.answer && (
                  <Typography variant="body1" sx={{ mt: 1 }}>{faq.answer}</Typography>
                )}
              </Box>
            </Box>
          );
        };

        return <AccordionItem key={key} />;
      })}
    </Box>
  );
};

export default FaqAccordion;
